/*  src/pages/StudentDashboard.jsx  */
import React, { useState, useEffect, useContext, useCallback } from 'react';
import API                   from '../services/api';
import { AuthContext }       from '../context/AuthContext';
import '../styles/Dashboard.css';

export default function StudentDashboard() {
  /* ───────── auth / state ───────── */
  const { user } = useContext(AuthContext);

  const [requests, setRequests]         = useState([]);
  const [userProfile, setUserProfile]   = useState(null);
  const [search, setSearch]             = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showPrev, setShowPrev]         = useState(false);

  /* modal helpers */
  const [showModal, setShowModal] = useState(false);
  const [showAffidavitAlert, setShowAffidavitAlert] = useState(false);
  const [loading,   setLoading]   = useState(false);
  const [modalReq,  setModalReq]  = useState(null);

  /* guardians (read-only) */
  const [guardians, setGuardians]               = useState([]);   // [{_id,name,relation,phone}]
  const [guardiansLoading, setGuardiansLoading] = useState(false);
  const [selectedGuardianId, setSelectedGuardianId] = useState('');
  const [triedGuardiansLoad, setTriedGuardiansLoad] = useState(false); // prevent repeated fetches

  /* create-request form */
  const initialForm = {
    reason:'', leavingWith:'self',
    transport:'public', vehicleNo:'', driverName:'',
    date:'', time:''
  };
  const [form, setForm]   = useState(initialForm);
  const [reasonErr, setReasonErr] = useState('');

  /* fetch once: requests and user profile */
  useEffect(() => {
    API.get('/requests').then(r => setRequests(r.data));
    API.get('/users/me').then(r => setUserProfile(r.data)).catch(err => console.error('Failed to load profile:', err));
  }, []);

  /* counters */
  const pendingCount  = requests.filter(r => r.status==='pending').length;
  const approvedCount = requests.filter(r => r.status==='approved').length;
  const rejectedCount = requests.filter(r => r.status==='rejected').length;
  const hasPending    = pendingCount > 0;

  /* split current / previous */
  const prevStatuses   = ['in','out'];
  const currentRows    = requests.filter(r => !prevStatuses.includes(r.status));
  const previousRows   = requests.filter(r =>  prevStatuses.includes(r.status));

  const rowMatches = r =>
    [r.leaveFor, r.pickUpWith, r.transport, r.vehicleNo]
      .some(f => String(f||'').toLowerCase().includes(search.toLowerCase()));

  const currentFiltered  = currentRows
    .filter(r => (statusFilter==='all'||r.status===statusFilter) && rowMatches(r));

  const previousFiltered = previousRows
    .filter(rowMatches)
    .sort((a,b)=>new Date(b.dateTime)-new Date(a.dateTime));

  /* ───────── helpers ───────── */
  const countWords = str =>
    str.trim()==='' ? 0 : str.trim().split(/\s+/).length;

  /* guardians loader (read-only) */
  const normalizeGuardianArray = (data) => {
    if (!data) return [];
    if (Array.isArray(data)) return data;

    // Accept multiple shapes
    const candidates = [
      data.guardians,
      data.guardians_info,                 // DB field
      data.user?.guardians,
      data.user?.guardians_info,
      data.profile?.guardians,
      data.profile?.guardians_info,
    ];
    for (const arr of candidates) {
      if (Array.isArray(arr) && arr.length) return arr;
    }
    return [];
  };

  const normalizeGuardianItem = (g, idx=0) => ({
    _id      : g?._id || g?.id || `${g?.name||'guardian'}-${g?.relation||'rel'}-${idx}`,
    name     : g?.name || g?.guardianName || '',
    relation : g?.relation || g?.guardianRel || g?.relationship || '',
    phone    : g?.phone || g?.guardianPhone || g?.contact || g?.mobile || '',
  });

  const loadGuardians = useCallback(async () => {
    if (triedGuardiansLoad || guardiansLoading || guardians.length > 0) return;

    setTriedGuardiansLoad(true);
    setGuardiansLoading(true);
    try {
      const tryEndpoints = [
        '/users/me/guardians', // new backend route
        '/users/guardians',    // alias
        '/auth/me',            // returns { user: ... }
        '/users/me',           // returns user doc
      ];
      let found = [];
      for (const url of tryEndpoints) {
        try {
          const { data } = await API.get(url);
          const arr = normalizeGuardianArray(data);
          if (arr.length) {
            found = arr.map((g, i) => normalizeGuardianItem(g, i)).filter(g => g.name);
            break;
          }
        } catch {
          // ignore each failed endpoint, try next
        }
      }
      setGuardians(found);
      if (found.length) {
        setSelectedGuardianId(found[0]._id);
      } else {
        setSelectedGuardianId('');
      }
    } finally {
      setGuardiansLoading(false);
    }
  }, [triedGuardiansLoad, guardiansLoading, guardians.length]);

  /* when opening modal + choosing guardian, attempt load ONCE */
  useEffect(() => {
    if (
      showModal &&
      form.leavingWith === 'guardian' &&
      !triedGuardiansLoad &&
      !guardiansLoading &&
      guardians.length === 0
    ) {
      loadGuardians();
    }
  }, [
    showModal,
    form.leavingWith,
    triedGuardiansLoad,
    guardiansLoading,
    guardians.length,
    loadGuardians
  ]);

  /* new-request submit */
  const submit = async e => {
    e.preventDefault();
    if (reasonErr) return;

    // derive pickUpWith based on selection
    let pickUpWith = 'Self';
    if (form.leavingWith === 'guardian') {
      if (guardians.length > 0) {
        const g = guardians.find(x => x._id === selectedGuardianId) || guardians[0];
        pickUpWith = `${g.name} (${g.relation || 'Guardian'})`;
      } else {
        // no guardian in DB but student chose "With Guardian" → still allow submission
        pickUpWith = 'Guardian';
      }
    }

    const payload = {
      leaveFor   : form.reason,
      pickUpWith,
      transport  : form.transport,
      vehicleNo  : form.transport==='public' ? '-' : form.vehicleNo,
      driverName : form.transport==='public' ? '-' : form.driverName,
      dateTime   : `${form.date}T${form.time}`
    };

    setLoading(true);
    try {
      await API.post('/requests', payload);
      const { data } = await API.get('/requests');
      setRequests(data);
      setShowModal(false);
      setForm(initialForm);
      setReasonErr('');
    } catch (err) {
      const msg = err?.response?.data?.msg || err.message || 'Failed to submit request';
      alert(msg);
    } finally {
      setLoading(false);
    }
  };

  /* reusable cell */
  const Cell = ({children}) => <td>{children}</td>;

  /* Affidavit Alert Modal */
  const AffidavitAlertModal = () => (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      backdropFilter: 'blur(4px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      animation: 'fadeIn 0.2s ease-out'
    }}>
      <div style={{
        backgroundColor: '#ffffff',
        borderRadius: '16px',
        padding: '32px',
        maxWidth: '480px',
        width: '90%',
        textAlign: 'center',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(0, 0, 0, 0.05)',
        animation: 'slideUp 0.3s ease-out',
        position: 'relative'
      }}>
        <div style={{
          width: '80px',
          height: '80px',
          margin: '0 auto 20px',
          backgroundColor: '#fef2f2',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '42px',
          border: '3px solid #fee2e2'
        }}>
          ⚠️
        </div>
        <h3 style={{
          margin: '0 0 12px',
          color: '#dc2626',
          fontSize: '24px',
          fontWeight: '600'
        }}>
          Affidavit Required
        </h3>
        <p style={{
          lineHeight: '1.7',
          marginBottom: '28px',
          color: '#4b5563',
          fontSize: '15px'
        }}>
          You are not eligible to leave university alone.<br/>
          Please contact your warden to submit your affidavit.
        </p>
        <button 
          className="action-btn blue" 
          onClick={() => setShowAffidavitAlert(false)}
          style={{
            minWidth: '140px',
            padding: '12px 24px',
            fontSize: '15px',
            fontWeight: '600',
            backgroundColor: '#3b82f6',
            border: 'none',
            borderRadius: '8px',
            color: 'white',
            cursor: 'pointer',
            transition: 'all 0.2s',
            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#2563eb'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#3b82f6'}
        >
          I Understand
        </button>
      </div>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { 
            opacity: 0;
            transform: translateY(20px) scale(0.95);
          }
          to { 
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>
    </div>
  );

  /* ─────────────────────────── UI ─────────────────────────── */
  return (
    <div className="dashboard-container">
      {showAffidavitAlert && <AffidavitAlertModal />}

      {/* header */}
      <div className="dashboard-header">
        <h2>Welcome, {user.name}</h2>
        <div className="counters">
          <div className="counter-item"><div>Pending</div><div>{pendingCount}</div></div>
          <div className="counter-item"><div>Approved</div><div>{approvedCount}</div></div>
          <div className="counter-item"><div>Rejected</div><div>{rejectedCount}</div></div>
        </div>
      </div>

      {/* create button */}
      <button
        className="action-btn"
        disabled={hasPending}
        onClick={() => {
          if (userProfile && !userProfile.affidavit) {
            setShowAffidavitAlert(true);
          } else {
            setShowModal(true);
          }
        }}
      >
        {hasPending ? 'Pending request in progress'
                     : 'Create New Leave Request'}
      </button>

      {/* filters */}
      <div className="search-filter">
        <input placeholder="Search requests…"
               value={search}
               onChange={e=>setSearch(e.target.value)} />
        <select value={statusFilter}
                onChange={e=>setStatusFilter(e.target.value)}>
          <option value="all">All</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
        <button className="view-btn" style={{marginLeft:'auto'}}
                onClick={()=>setShowPrev(p=>!p)}>
          {showPrev ? 'Hide Previous' : 'Show Previous'}
        </button>
      </div>

      {/* current table */}
      <div className="table-container prettified" style={{marginBottom:24}}>
        <h3 style={{margin:'8px 0'}}>Current Requests</h3>
        <table>
          <thead>
            <tr>
              <th>Date &amp; Time</th><th>Reason</th><th>Leaving With</th>
              <th>Transport</th><th>Vehicle No</th><th>Status</th><th>Action</th>
            </tr>
          </thead>
          <tbody>
          {currentFiltered.length ? currentFiltered.map(r=>(
            <tr key={r._id}>
              <Cell>{new Date(r.dateTime).toLocaleString()}</Cell>
              <Cell>{r.leaveFor}</Cell>
              <Cell>{r.pickUpWith}</Cell>
              <Cell>{r.transport}</Cell>
              <Cell>{r.vehicleNo}</Cell>
              <Cell><span className={`status-badge status-${r.status}`}>{r.status}</span></Cell>
              <Cell>
                <button className="view-btn" onClick={()=>setModalReq(r)}>View Comment</button>
              </Cell>
            </tr>
          )) : <tr><td colSpan={7} style={{textAlign:'center',padding:16}}>No records</td></tr>}
          </tbody>
        </table>
      </div>

      {/* previous table */}
      {showPrev && (
        <div className="table-container prettified">
          <h3 style={{margin:'8px 0'}}>Previous Requests (IN / OUT)</h3>
          <table>
            <thead>
              <tr>
                <th>Date &amp; Time</th><th>Reason</th><th>Leaving With</th>
                <th>Transport</th><th>Vehicle No</th><th>Status</th>
              </tr>
            </thead>
            <tbody>
            {previousFiltered.length ? previousFiltered.map(r=>(
              <tr key={r._id}>
                <Cell>{new Date(r.dateTime).toLocaleString()}</Cell>
                <Cell>{r.leaveFor}</Cell>
                <Cell>{r.pickUpWith}</Cell>
                <Cell>{r.transport}</Cell>
                <Cell>{r.vehicleNo}</Cell>
                <Cell><span className={`status-badge status-${r.status}`}>{r.status}</span></Cell>
              </tr>
            )) : <tr><td colSpan={6} style={{textAlign:'center',padding:16}}>No IN/OUT records</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* comment modal */}
      {modalReq && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header"><h3>Warden’s Comment</h3></div>
            <div className="modal-body">{modalReq.wardenComment || 'No comment provided.'}</div>
            <div className="modal-footer">
              <button className="close-btn" onClick={()=>setModalReq(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* new-request modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal large">
            <div className="modal-header"><h3>New Leave Request</h3></div>

            <form className="modal-body" onSubmit={submit}>
              {/* reason */}
              <div className="form-group">
                <label>
                  Reason for Leave
                  <small className="hint">
                    &nbsp;— Please write a short reason (max&nbsp;20&nbsp;words), e.g.&nbsp;
                    “Going home on weekend”
                  </small>
                </label>
                <input
                  value={form.reason}
                  onChange={e => {
                    const v = e.target.value;
                    setForm({...form, reason:v});
                    const words = countWords(v);
                    setReasonErr(words > 20 ? 'Maximum 20 words allowed' : '');
                  }}
                  required
                />
                {reasonErr && <small className="error">{reasonErr}</small>}
              </div>

              {/* leaving with */}
              <div className="form-group">
                <label>Leaving With</label>
                <select
                  value={form.leavingWith}
                  onChange={e=>{
                    const v = e.target.value;
                    setForm({...form,leavingWith:v});
                    if (
                      v === 'guardian' &&
                      !triedGuardiansLoad &&
                      !guardiansLoading &&
                      guardians.length === 0
                    ) {
                      loadGuardians();
                    }
                  }}
                >
                  <option value="self">Self</option>
                  <option value="guardian">With Guardian</option>
                </select>
              </div>

              {/* guardians section (read-only) */}
              {form.leavingWith==='guardian' && (
                <>
                  {guardiansLoading && (
                    <div className="form-group">
                      <small className="hint">Loading saved guardians…</small>
                    </div>
                  )}

                  {(!guardiansLoading && guardians.length > 0) ? (
                    <div className="form-group">
                      <label>Select Saved Guardian</label>
                      <select
                        value={selectedGuardianId}
                        onChange={e=>setSelectedGuardianId(e.target.value)}
                      >
                        {guardians.map(g=>(
                          <option key={g._id} value={g._id}>
                            {g.name} {g.relation ? `(${g.relation})` : ''} {g.phone ? `— ${g.phone}` : ''}
                          </option>
                        ))}
                      </select>
                      <small className="hint">
                        The selected guardian’s name &amp; relation will be attached to this request.
                      </small>
                    </div>
                  ) : (
                    !guardiansLoading && (
                      <div className="form-group">
                        <div className="inline-note error">
                          No guardian info found. Please ask the IT Department to add your guardian information.
                        </div>
                      </div>
                    )
                  )}
                </>
              )}

              {/* transport */}
              <div className="form-group">
                <label>Transport</label>
                <select value={form.transport}
                        onChange={e=>setForm({...form,transport:e.target.value})}>
                  <option value="public">Public</option>
                  <option value="private">Private</option>
                </select>
              </div>

              {form.transport==='private' && (
                <>
                  <div className="form-group">
                    <label>Vehicle Number</label>
                    <input value={form.vehicleNo}
                           onChange={e=>setForm({...form,vehicleNo:e.target.value})}
                           required/>
                  </div>
                  <div className="form-group">
                    <label>Driver Name</label>
                    <input value={form.driverName}
                           onChange={e=>setForm({...form,driverName:e.target.value})}
                           required/>
                  </div>
                </>
              )}

              {/* date & time */}
              <div className="form-row">
                <div className="form-group">
                  <label>Date (leaving)</label>
                  <input type="date" value={form.date}
                         onChange={e=>setForm({...form,date:e.target.value})} required/>
                </div>
                <div className="form-group">
                  <label>Time</label>
                  <input type="time" value={form.time}
                         onChange={e=>setForm({...form,time:e.target.value})} required/>
                </div>
              </div>

              {/* footer */}
              <div className="modal-footer">
                <button type="submit"
                        disabled={loading || !!reasonErr}>
                  {loading ? 'Submitting…' : 'Submit'}
                </button>
                <button type="button" className="cancel-btn-form"
                        onClick={()=>setShowModal(false)} disabled={loading}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
          {loading && <div className="loading-box">Sending request…</div>}
        </div>
      )}
    </div>
  );
}

/* ─── tiny CSS additions (append to Dashboard.css if not already present) ───
.hint  { font-size:12px; color:#64748b; font-weight:400; }
.error { color:#e11d48; font-size:12px; }
.inline-note.error{
  background:#fef2f2;
  border:1px solid #fecaca;
  color:#991b1b;
  padding:8px 10px;
  border-radius:8px;
  font-size:13px;
}
*/
